/**
 * A file as the legacy gallery fields hold it.
 *
 * These fields are typed by the GraphQL queries that used to feed them — `FileCardGalleryFragment`,
 * `GetFilesDataItem` — and those types have **no `laravel_id`**: on the `File` graph type the
 * numeric backend id *is* `id`, carried as a string. A field that has been picked into during this
 * session holds a `PickerFile` instead, which has both. This shape is the union of the two.
 *
 * Loose on purpose. `CoverImage` takes its value as `any` and several call sites hand these fields
 * whatever their form state happens to hold, so tightening this would only move the casts.
 */
export type GalleryFileLike = {
  id?: string | number | null;
  laravel_id?: number | null;
  name?: string | null;
  type?: string | null;
  extension?: string | null;
  size?: string | number | null;
  /**
   * Set when the file is served straight from a public CDN — a Sanity asset, not a file the backend
   * has a record of. Its presence is what marks a file the backend knows nothing about.
   */
  public_url?: string | null;
};

/**
 * The numeric backend id of a gallery file, or undefined when it has none.
 *
 * `laravel_id` first for a value that came back from the picker, then `id` for one that came from
 * GraphQL. Anything that isn't a positive integer is *not* a backend id — the boat gallery renders
 * Sanity assets whose id is an asset ref (`image-abc-123`) or a `_key`, and a row still syncing has
 * no id at all.
 *
 * `Number` rather than `parseInt`, which would read `image-…` as a number wherever it happened to
 * start with a digit.
 *
 * This is the one predicate that decides whether a file can be addressed on the backend at all, so
 * both things that need to ask share it: attaching it (`pickerFileFromGalleryFile`) and opening its
 * details (`Gallery`).
 */
export const galleryFileBackendId = (file: GalleryFileLike): number | undefined => {
  // A public CDN file is not a backend file even if its id happens to look numeric — a Sanity
  // `_key` is an arbitrary string and can be all digits.
  if (file.public_url) {
    return undefined;
  }

  const candidate = typeof file.laravel_id === 'number' ? file.laravel_id : Number(file.id);

  return Number.isInteger(candidate) && candidate > 0 ? candidate : undefined;
};
